import {
    MAP_GROUP_ID_TO_SALESCHANNEL,
    SAILS_BNT_STORAGE,
    SAILS_PARAM_SALESCHANNEL,
    SAILS_PARAM_SUB_ID,
    SalesChannel,
    SALESCHANNEL_CONSUMER,
    URL_PARAM_SUBGROUP_ID,
    MAP_SALESCHANNEL_TO_GROUP_ID,
    SALESCHANNEL_FAMILY_FRIENDS,
    SALESCHANNEL_REDPLUS,
    REDPLUS_STORAGE,
    REDPLUS_ALLNET_GROUP,
    SALESCHANNEL_SOHO,
    SAILS_PARAM_GIGAKOMBI,
    RLZ_SOC,
    SAILS_PARAM_RLZ,
} from '@vfde-fe/sails-constants';
import {
    getSessionStorage,
    getURLParam,
} from '@vfde-fe/sails_core';
import {
    APP,
    CONTAINER_TARIFF,
} from 'Constant';
import {
    GIGAKOMBI_TOKEN_PARAM,
    GIGAKOMBI_URL_PARAM,
} from '../../DeviceDetail/constants';
import { SailsHardwareDetailsStorage } from '../../DeviceDetail/interface';

/**
 * Get the subscription ID from URL or session storage and validate against saleschannel
 */
export const getSubscriptionId = (salesChannel: SalesChannel = SALESCHANNEL_CONSUMER): string => {
    // Default subscription id
    let subscriptionId = getSubscriptionFromUrl(salesChannel) ?? getSubscriptionFromStorage();

    // Redplus subscription id
    if (salesChannel === SALESCHANNEL_REDPLUS) {
        subscriptionId = getSubscriptionFromUrl(SALESCHANNEL_REDPLUS) ?? getSubscriptionFromRedplusStorage() ?? getSubscriptionFromStorage();
    }

    if (subscriptionId && validateSubscriptionId(subscriptionId, salesChannel)) {
        return subscriptionId;
    }

    // For redplus: if no valid subscriptionId, or subscriptionId is not redplus, we return redplus allnet as default
    if (salesChannel === SALESCHANNEL_REDPLUS) {
        return `${REDPLUS_ALLNET_GROUP}`;
    }
};

/**
 * Get subscription ID from URL
 */
export const getSubscriptionFromUrl = (salesChannel?: SalesChannel): string => {
    const urlParamSubGroupId = getURLParam(URL_PARAM_SUBGROUP_ID);

    if (salesChannel && urlParamSubGroupId && urlParamSubGroupId in MAP_GROUP_ID_TO_SALESCHANNEL && MAP_GROUP_ID_TO_SALESCHANNEL[urlParamSubGroupId].indexOf(salesChannel) !== -1) {
        return urlParamSubGroupId;
    }

    if (!salesChannel && urlParamSubGroupId && urlParamSubGroupId in MAP_GROUP_ID_TO_SALESCHANNEL) {
        return urlParamSubGroupId;
    }
};

/**
 * Get subscription ID from storage
 */
export const getSubscriptionFromStorage = (): string => {
    const storage: SailsHardwareDetailsStorage = getSessionStorage(SAILS_BNT_STORAGE);

    return storage[APP]?.[CONTAINER_TARIFF]?.[SAILS_PARAM_SUB_ID]?.[storage[APP]?.[CONTAINER_TARIFF]?.[SAILS_PARAM_SALESCHANNEL]] ?? storage[SAILS_PARAM_SUB_ID]?.[storage[SAILS_PARAM_SALESCHANNEL]];
};

/**
 * Get Redplus subscription ID from storage
 */
export const getSubscriptionFromRedplusStorage = (): string => {
    const storage = getSessionStorage(REDPLUS_STORAGE);

    return storage[SAILS_PARAM_SUB_ID];
};

/**
 * Get the saleschannel from given subscription ID. Returns default "consumer".
 */
export const getSaleschannel = (): SalesChannel => {
    return getSalesChannelFromUrl() ?? getSalesChannelFromStorage() ?? getSalesChannelFromSubscription() ?? SALESCHANNEL_CONSUMER;
};

/**
 * Get Saleschannel from URL
 */
export const getSalesChannelFromUrl = (): SalesChannel => {
    const salesChannelFromUrl = getURLParam(SAILS_PARAM_SALESCHANNEL);

    if (Object.keys(MAP_SALESCHANNEL_TO_GROUP_ID).indexOf(salesChannelFromUrl) !== -1) {
        return salesChannelFromUrl as SalesChannel;
    }
};

/**
 * Get saleschannel from storage
 */
export const getSalesChannelFromStorage = (): SalesChannel => {
    const storage = getSessionStorage(SAILS_BNT_STORAGE);

    return storage[APP]?.[CONTAINER_TARIFF]?.[SAILS_PARAM_SALESCHANNEL] ?? storage?.[SAILS_PARAM_SALESCHANNEL];
};

/**
 * Get sales channel from subscription ID
 */
export const getSalesChannelFromSubscription = (): SalesChannel => {
    return MAP_GROUP_ID_TO_SALESCHANNEL[getSubscriptionFromUrl()]?.[0];
};

/**
 * Get gigakombi from session storage
 */
export const getGigakombi = (salesChannel: string): boolean => {
    if (salesChannel === SALESCHANNEL_FAMILY_FRIENDS || salesChannel === SALESCHANNEL_REDPLUS) {
        return false;
    }

    const storage = getSessionStorage(SAILS_BNT_STORAGE);

    return getGigakombiFromUrl() ?? getGigakombiFromStorage(storage) ?? false;
};

const getGigakombiFromUrl = (): true => {
    const gigakombiParam = getURLParam(GIGAKOMBI_URL_PARAM);

    return (gigakombiParam === '1' || gigakombiParam === 'true' || undefined) ?? (!!getURLParam(GIGAKOMBI_TOKEN_PARAM) || undefined);
};

const getGigakombiFromStorage = (storage): boolean => {
    return storage[SAILS_PARAM_GIGAKOMBI] ?? storage[APP]?.[CONTAINER_TARIFF]?.[SAILS_PARAM_GIGAKOMBI];
};

/**
 * Validate subscription ID
 * @param id
 * @param salesChannel
 */
export const validateSubscriptionId = (id: string, salesChannel: SalesChannel): boolean => {
    return MAP_SALESCHANNEL_TO_GROUP_ID[salesChannel].indexOf(Number(id)) !== -1;
};

/**
 * Get eligibility token
 */
export const getGigakombiEligibilityToken = (): string => {
    return getURLParam(GIGAKOMBI_TOKEN_PARAM);
};

/**
 * Get Restlaufzeit from storage
 * @returns boolean
 */
export const getRestlaufzeit = (): boolean => {
    const storage = getSessionStorage(SAILS_BNT_STORAGE);

    return storage?.[SAILS_PARAM_RLZ] === RLZ_SOC;
};
